import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["as", "children", "color", "display", "marginBlockEnd", "marginBlockStart", "marginInlineStart", "paddingBlock", "paddingBlockEnd", "paddingBlockStart", "paddingInline", "textAlign", "textDecoration", "typography", "use", "visuallyHidden", "whiteSpace", "wordBreak"];
import * as stylex from '@stylexjs/stylex';
import { Text } from 'foundations-components/components/Text';
import { TextRoot } from 'trellis-components/components/Text/primitives';
import { unsafeXStyle } from 'trellis-components/utils/unsafeXStyle';
import { theme } from 'trellis-theming/theme.stylex';
import { useUiVariant } from './UiVariantProvider';
import { toMarginXstyle, toPaddingXstyle } from './utils/foundationsSpacing';
import { jsx as _jsx } from "react/jsx-runtime";
const VARIANT_BY_TYPOGRAPHY = {
  'body-100': 'body-md',
  'body-125': 'heading-5',
  'detail-100': 'body-sm',
  'detail-200': 'body-sm',
  'heading-25': 'body-sm',
  'heading-50': 'heading-5',
  'heading-100': 'heading-5',
  'heading-300': 'heading-4'
};
const VARIANT_BY_ELEMENT = {
  h2: 'heading-3',
  h5: 'heading-5',
  p: 'body-md',
  small: 'body-sm',
  span: 'inherit'
};

// TRELLIS-TODO: the public Trellis theme has no error, interactive or disabled
// text color, so those colors inherit until semantic fg tokens are available.
const styles = {
  inheritColor: {
    color: "x1heor9g",
    $$css: true
  },
  subtle: {
    color: "x9fihvt",
    $$css: true
  },
  block: {
    display: "x1lliihq",
    $$css: true
  },
  textAlignCenter: {
    textAlign: "x2b8uid",
    $$css: true
  },
  underline: {
    textDecoration: "x1bvjpef",
    textDecorationColor: null,
    textDecorationLine: null,
    textDecorationStyle: null,
    textDecorationThickness: null,
    $$css: true
  },
  whiteSpaceNormal: {
    whiteSpace: "xeaf4i8",
    $$css: true
  },
  whiteSpacePreWrap: {
    whiteSpace: "x126k92a",
    $$css: true
  },
  breakWord: {
    wordBreak: "x13faqbe",
    $$css: true
  },
  visuallyHidden: {
    height: "xjm9jq1",
    borderWidth: "xc342km",
    borderInlineWidth: null,
    borderInlineStartWidth: null,
    borderLeftWidth: null,
    borderInlineEndWidth: null,
    borderRightWidth: null,
    borderBlockWidth: null,
    borderTopWidth: null,
    borderBottomWidth: null,
    clip: "xzpqnlu",
    width: "x1i1rx1s",
    marginBlock: "x16sahtb",
    marginTop: null,
    marginBottom: null,
    marginInline: "xz95peq",
    marginInlineStart: null,
    marginLeft: null,
    marginInlineEnd: null,
    marginRight: null,
    overflow: "xb3r6kr",
    overflowX: null,
    overflowY: null,
    paddingBlock: "xt970qd",
    paddingTop: null,
    paddingBottom: null,
    paddingInline: "xnjsko4",
    paddingStart: null,
    paddingLeft: null,
    paddingEnd: null,
    paddingRight: null,
    position: "x10l6tqk",
    whiteSpace: "xuxw1ft",
    $$css: true
  }
};
const TrellisText = _ref => {
  let {
      as,
      children,
      color,
      display,
      marginBlockEnd,
      marginBlockStart,
      marginInlineStart,
      paddingBlock,
      paddingBlockEnd,
      paddingBlockStart,
      paddingInline,
      textAlign,
      textDecoration,
      typography,
      visuallyHidden,
      whiteSpace,
      wordBreak
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(TextRoot, Object.assign({}, rest, {
    as: as === 'small' ? 'span' : as !== null && as !== void 0 ? as : 'p',
    variant: typography ? VARIANT_BY_TYPOGRAPHY[typography] : VARIANT_BY_ELEMENT[as !== null && as !== void 0 ? as : 'p'],
    xstyle: unsafeXStyle([color === 'text-core-subtle' || color === 'do-not-use-slinky' ? styles.subtle : styles.inheritColor, display === 'block' && styles.block, textAlign === 'center' && styles.textAlignCenter, textDecoration === 'underline' && styles.underline, whiteSpace === 'normal' && styles.whiteSpaceNormal, whiteSpace === 'pre-wrap' && styles.whiteSpacePreWrap, wordBreak === 'break-word' && styles.breakWord, toMarginXstyle({
      marginBlockEnd,
      marginBlockStart,
      marginInlineStart
    }), toPaddingXstyle({
      paddingBlock,
      paddingBlockEnd,
      paddingBlockStart,
      paddingInline
    }), visuallyHidden && styles.visuallyHidden]),
    children: children
  }));
};
export const CdpText = props => {
  const variant = useUiVariant();
  return variant === 'trellis' ? /*#__PURE__*/_jsx(TrellisText, Object.assign({}, props)) : /*#__PURE__*/_jsx(Text, Object.assign({}, props));
};